/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';

import { Grid, Typography, makeStyles } from '@material-ui/core';
import i18n from 'i18n';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import SettingsIcon from '@material-ui/icons/Settings';
import { history } from 'store';
import { setSelectedPartner } from 'modules/partners/actions';
import CloseIcon from '@material-ui/icons/Close';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import DoneIcon from '@material-ui/icons/Done';
import Button from 'components/Button';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { AllPartnersFields } from 'modules/partners/interfaces';
import ErrorIcon from '@material-ui/icons/Error';
import { Formik, Form } from 'formik';
import DynamicTextField from 'components/DynamicTextField';
import TitleDivider from 'components/TitleDivider';
import { EnvironmentFields } from 'modules/environments/interfaces';

import Permission from 'components/ValidPermission';
import {
  migrateEnvironmentRequest,
  setMigrateErrors,
} from 'modules/environments/actions';
import clsx from 'clsx';
import VerificationCode from 'components/VerificationCode';
import PartnerList from './partnerList';
import styles from '../styles';
import screenFields from './screenFields';
import schema from './schema';

export interface PartnerFieldsProps {
  partner_name?: string;
  partner_uuid?: string;
}

interface AdminProps {
  partnerSelected: PartnerData | null;
  companySelected: CompanyData | null;
  selectedPartner: AllPartnersFields | null;
  environmentSelected: EnvironmentFields | null;
  migrateErrors: string[] | null;
  isLoading: boolean;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setSelectedPartner: (prop: AllPartnersFields | null) => void;
  migrateEnvironmentRequest: (props) => void;
  setMigrateErrors: (setMigrateErrors: string[] | null) => void;
}

const useStyles = makeStyles(styles);

const ChangeAccount = ({
  partnerSelected,
  selectedPartner,
  companySelected,
  environmentSelected,
  migrateErrors,
  isLoading,
  modalOpen,
  modalClose,
  setSelectedPartner,
  migrateEnvironmentRequest,
  setMigrateErrors,
}: AdminProps) => {
  const classes = useStyles();
  const [
    partnerFields,
    setPartnerFields,
  ] = React.useState<PartnerFieldsProps | null>({
    partner_name: partnerSelected?.name,
    partner_uuid: partnerSelected?.id,
  });

  React.useEffect(() => {
    setSelectedPartner(null);
  }, []);

  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill('')
  );

  const verificationCodeRef = React.useRef(verificationCode);

  React.useEffect(() => {
    verificationCodeRef.current = verificationCode;
  }, [verificationCode]);

  const handleConfirm = (values: any) => {
    const verificationCodeToSend = verificationCodeRef.current;
    migrateEnvironmentRequest({
      environmentUuid: environmentSelected?.environment_uuid,
      partnerUuid: selectedPartner?.partner_uuid,
      reason: values?.reason,
      companyUuidToRedirect: selectedPartner?.company_uuid,
      companyNameToRedirect: selectedPartner?.company_name,
      partnerNameToRedirect: selectedPartner?.partner_name,
      code: verificationCodeToSend.join(''),
    });
    modalClose();
  };

  const handleSubmit = (values: any) => {
    setVerificationCode(Array(6).fill(''));
    migrateEnvironmentRequest({
      environmentUuid: environmentSelected?.environment_uuid,
      partnerUuid: selectedPartner?.partner_uuid,
      reason: values?.reason,
    });
    modalOpen({
      title: i18n.t('VERIFICATION_CODE_KEY'),
      content: (
        <VerificationCode
          length={6}
          setVerificationCode={setVerificationCode}
          onRetry={() => {
            migrateEnvironmentRequest({
              environmentUuid: environmentSelected?.environment_uuid,
              partnerUuid: selectedPartner?.partner_uuid,
              reason: values?.reason,
            });
          }}
          onConfirm={() => {
            handleConfirm(values);
          }}
        />
      ),
      fullWidth: true,
    });
  };

  setMigrateErrors(null);
  React.useEffect(() => {
    setMigrateErrors(migrateErrors);
  }, [migrateErrors]);

  const handleChoosePartner = () => {
    modalOpen({
      title: i18n.t('CHOOSE_PARTNER_KEY'),
      content: (
        <PartnerList
          companyFields={partnerFields}
          setCompanyFields={setPartnerFields}
        />
      ),
      maxWidth: 'md',
      fullWidth: true,
      footer: (
        <Grid>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleMigrateEnv = () => {
    modalOpen({
      title: i18n.t('MIGRATION_OF_ENVIRONMENT_KEY', {
        environment_name: environmentSelected?.name,
      }),
      content: (
        <>
          <Grid container>
            <Typography className={classes.spacing}>
              {i18n.t('MIGRATION_MODAL_TEXT_KEY')}
            </Typography>
            <Typography className={classes.boldText}>
              {environmentSelected?.name}
            </Typography>
          </Grid>
          <Grid container className={classes.spacingGrid}>
            <Typography className={classes.boldText}>
              {i18n.t('MIGRATION_ORIGIN_KEY')}
            </Typography>
            <Grid container>
              <Typography className={classes.spacing}>
                {i18n.t('MIGRATION_PARTNER_KEY')}
              </Typography>
              <Typography className={classes.boldText}>
                {partnerSelected?.name}
              </Typography>
            </Grid>
            <Grid container>
              <Typography className={classes.spacing}>
                {i18n.t('MIGRATION_COMPANY_KEY')}
              </Typography>
              <Typography className={classes.boldText}>
                {companySelected?.name}
              </Typography>
            </Grid>
          </Grid>

          <Grid container className={classes.spacingGrid}>
            <Typography className={classes.boldText}>
              {i18n.t('MIGRATION_DESTINATION_KEY')}
            </Typography>
            <Grid container>
              <Typography className={classes.spacing}>
                {i18n.t('MIGRATION_PARTNER_KEY')}
              </Typography>
              <Typography className={classes.boldText}>
                {selectedPartner?.partner_name}
              </Typography>
            </Grid>
            <Grid container>
              <Typography className={classes.spacing}>
                {i18n.t('MIGRATION_COMPANY_KEY')}
              </Typography>
              <Typography className={classes.boldText}>
                {selectedPartner?.company_name}
              </Typography>
            </Grid>
          </Grid>

          <TitleDivider overrideClasses={{ title: { marginTop: 8 } }}>
            {i18n.t('MIGRATION_TO_CONFIRM_KEY')}
          </TitleDivider>
          <Formik
            initialValues={{ reason: '' }}
            enableReinitialize
            validationSchema={schema}
            validateOnChange
            validateOnBlur
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, errors, values, submitForm }) => {
              return (
                <React.Fragment>
                  <Form>
                    <Grid container>
                      <Grid item xs={12} spacing={2} className={classes.fields}>
                        <DynamicTextField
                          {...screenFields[0]}
                          setFieldValue={setFieldValue}
                          value={values?.reason}
                          errors={errors}
                        />
                      </Grid>

                      <Grid
                        container
                        justify="flex-end"
                        className={classes.deleteButtons}
                      >
                        <Button
                          submitButton
                          id="confirm-button"
                          onClick={submitForm}
                          isLoading={isLoading}
                        >
                          {i18n.t('CONTINUE_KEY')}
                        </Button>
                        <Button
                          id="cancel-button"
                          variant="outlined"
                          onClick={modalClose}
                        >
                          {i18n.t('CANCEL_KEY')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                </React.Fragment>
              );
            }}
          </Formik>
        </>
      ),
    });
  };

  return (
    <>
      <Grid container className={classes.changeAccountContainer}>
        <Grid item xs={4}>
          <Typography className={classes.currentAccount}>
            {i18n.t('CURRENT_ACCOUNT_KEY')}
          </Typography>
          <Typography className={classes.accountName}>
            {partnerSelected?.name}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <div className={classes.arrow}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </Grid>
        <Grid item xs={4}>
          <Typography className={classes.newAccount}>
            {i18n.t('NEW_ACCOUNT_KEY')}
          </Typography>
          <Button
            id={'btn-select-partner'}
            onClick={() => {
              handleChoosePartner();
              setMigrateErrors(null);
            }}
            className={clsx(classes.autocompleteCompany, {
              [classes.errorSamePartnerButton]:
                selectedPartner?.partner_uuid === partnerSelected?.id,
            })}
          >
            {selectedPartner
              ? selectedPartner?.partner_name
              : i18n.t('CHOOSE_PARTNER_KEY')}
          </Button>
        </Grid>
        {selectedPartner?.partner_uuid === partnerSelected?.id && (
          <Grid container justify="flex-end">
            <Typography className={classes.errorSamePartner}>
              {i18n.t('ORIGIN_AND_DESTINATION_PARTNER_CANT_BE_EQUAL')}
            </Typography>
          </Grid>
        )}
        <Grid
          container
          justify="space-between"
          className={classes.buttonBottom}
        >
          <Grid item xs={11} className={classes.gridWarnToMigrateTitle}>
            <ErrorOutlineIcon className={classes.warnToMigrate} />
            <Typography className={classes.warnToMigrateTextTitle}>
              {i18n.t('WARN_MIGRATION_RULES_KEY')}
            </Typography>
          </Grid>
          {migrateErrors ? (
            <Grid container className={classes.rulesGrid}>
              {migrateErrors.includes('provider_aws_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_ONE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_ONE_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_ONE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_ONE_KEY')}
                  </Typography>
                </Grid>
              )}

              {migrateErrors.includes('provider_oci_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_TWO_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_TWO_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_TWO_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_TWO_KEY')}
                  </Typography>
                </Grid>
              )}

              {migrateErrors.includes('active_directory_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_THREE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_THREE_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_THREE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_THREE_KEY')}
                  </Typography>
                </Grid>
              )}

              {migrateErrors.includes('salt_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_FOUR_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_FOUR_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_FOUR_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_FOUR_KEY')}
                  </Typography>
                </Grid>
              )}

              {migrateErrors.includes('sso_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_FIVE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_FIVE_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_FIVE_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_FIVE_KEY')}
                  </Typography>
                </Grid>
              )}
              {migrateErrors.includes('new_front_settings') ? (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextError}>
                    <CloseIcon className={classes.closeIconRule} />
                    <Typography className={classes.ruleBoldRed}>
                      {i18n.t('RULE_SIX_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_SIX_KEY')}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={11} className={classes.gridWarnToMigrate}>
                  <Typography className={classes.warnToMigrateTextDone}>
                    <DoneIcon className={classes.doneIconRule} />
                    <Typography className={classes.ruleBoldGreen}>
                      {i18n.t('RULE_SIX_KEY')}
                    </Typography>
                    {i18n.t('RULE_MIGRATION_SIX_KEY')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container className={classes.rulesGrid}>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_ONE_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_ONE_KEY')}
                </Typography>
              </Grid>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_TWO_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_TWO_KEY')}
                </Typography>
              </Grid>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_THREE_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_THREE_KEY')}
                </Typography>
              </Grid>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_FOUR_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_FOUR_KEY')}
                </Typography>
              </Grid>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_FIVE_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_FIVE_KEY')}
                </Typography>
              </Grid>
              <Grid item xs={11} className={classes.gridWarnToMigrate}>
                <Typography className={classes.warnToMigrateText}>
                  {'• '}
                  <Typography className={classes.ruleBold}>
                    {i18n.t('RULE_SIX_KEY')}
                  </Typography>{' '}
                  {i18n.t('RULE_MIGRATION_SIX_KEY')}
                </Typography>
              </Grid>
            </Grid>
          )}

          {selectedPartner && (
            <Grid item xs={2} className={classes.cardButtons}>
              <Button
                submitButton
                className={clsx(classes.buttonConfirm, {
                  [classes.buttonConfirmDisabled]:
                    selectedPartner?.partner_uuid === partnerSelected?.id,
                })}
                id="confirm-button"
                disabled={selectedPartner?.partner_uuid === partnerSelected?.id}
                onClick={() => {
                  handleMigrateEnv();
                }}
              >
                {i18n.t('CONFIRM_KEY')}
              </Button>
              <Button
                className={classes.buttonClean}
                id="clear-button"
                variant="outlined"
                onClick={() => setSelectedPartner(null)}
              >
                {i18n.t('CLEAR_FORM_KEY')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      {migrateErrors && (
        <Grid container className={classes.changeAccountContainerError}>
          <Grid container justify="flex-end">
            <CloseIcon
              className={classes.closeIcon}
              onClick={() => setMigrateErrors(null)}
            />
          </Grid>
          <Grid container className={classes.errorTitle}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography className={classes.errorText}>
              {i18n.t('ERROR_MIGRATION_RULES_KEY')}
            </Typography>
          </Grid>
          {migrateErrors.map(item => (
            <Grid className={classes.errorItemsGrid} container key={item}>
              <Typography className={classes.errorTextDescription}>
                {'• '}
                {i18n.t(`${item?.toUpperCase()}_KEY`)}
              </Typography>
            </Grid>
          ))}
          <Grid container className={classes.warnTitle}>
            <Typography className={classes.warnText}>
              {i18n.t('ERROR_MIGRATION_RULES_SOLUTION_KEY')}
            </Typography>
          </Grid>
          <Grid container className={classes.warnButtonGrid} justify="flex-end">
            <Permission value="edit_partner_details">
              <Button
                id="go-to-config"
                className={classes.goConfigButton}
                startIcon={<SettingsIcon />}
                variant="outlined"
                onClick={() => {
                  history.push(
                    `/partners/${partnerSelected?.id}/configuration`
                  );
                }}
              >
                {i18n.t('CURRENT_PARTNER_CONFIG_KEY')}
              </Button>
            </Permission>
            <Button
              id="discard-button"
              className={classes.warnButton}
              variant="outlined"
              onClick={() => setMigrateErrors(null)}
            >
              {i18n.t('DISMISS_KEY')}
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const mapStateToProps = ({
  authenticate: { partnerSelected, companySelected },
  partners: { selectedPartner },
  environments: { migrateErrors, isLoading },
}: ApplicationState) => ({
  partnerSelected,
  companySelected,
  selectedPartner,
  migrateErrors,
  isLoading,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  setSelectedPartner,
  migrateEnvironmentRequest,
  setMigrateErrors,
})(ChangeAccount);
